import React from 'react';
import { Menu, User, X } from 'lucide-react';
import { useShellNav } from '../../contexts/ShellNavContext';
import { usePageTitleContext } from '../../contexts/PageTitleContext';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationBell } from '../notifications/NotificationBell';
import { TenantSwitcher } from '../tenant/TenantSwitcher';
import { getActiveCompanyName } from '../../utils/tenant';
import { formatRole } from '../../utils/roleLabels';
import { isPlatformOwner } from '../../utils/platform';

type AppNavbarProps = {
  /** Hide tenant switcher / user chip in demo sandbox */
  sandbox?: boolean;
};

/**
 * Global app top bar: mobile menu + page title (left), notifications + workspace (right).
 * Designed so the hamburger never overlaps the title on small screens.
 */
export const AppNavbar: React.FC<AppNavbarProps> = ({ sandbox = false }) => {
  const { isSidebarOpen, toggleSidebar } = useShellNav();
  const { title } = usePageTitleContext();
  const { user, activeCompanyId, memberships, membershipRole } = useAuth();

  const companyLabel =
    getActiveCompanyName(memberships, activeCompanyId) ??
    (isPlatformOwner(user) ? 'Platform' : null);
  const displayName = user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || 'User'
    : 'User';
  const initials = `${(user?.firstName?.[0] || 'U').toUpperCase()}${(user?.lastName?.[0] || '').toUpperCase()}`;

  return (
    <header className="sticky top-0 z-50 border-b border-[#E0DCE5] bg-white/95 backdrop-blur-md">
      <div className="flex h-14 items-center gap-3 px-3 sm:px-4 md:px-6">
        {/* Mobile menu — reserved slot so title never sits under the control */}
        <button
          type="button"
          onClick={toggleSidebar}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#E0DCE5] bg-white text-[#261D30] shadow-sm transition hover:bg-[#F5F3F7] md:hidden"
          aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isSidebarOpen}
          aria-controls={sandbox ? 'demo-sidebar' : 'app-sidebar'}
        >
          {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>

        <div className="min-w-0 flex-1">
          <h1 className="truncate text-base font-semibold tracking-tight text-[#261D30] sm:text-lg">
            {title}
          </h1>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {!sandbox && <NotificationBell />}

          {!sandbox && isPlatformOwner(user) && memberships.length > 1 && (
            <div className="hidden max-w-[200px] lg:block">
              <TenantSwitcher variant="navbar" />
            </div>
          )}

          {!sandbox && user && (
            <div
              className="hidden items-center gap-2 rounded-lg border border-[#E0DCE5] bg-white px-2 py-1.5 sm:inline-flex"
              title={`${displayName}${companyLabel ? ` · ${companyLabel}` : ''}`}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#5C2E6B] to-[#F98C10] text-[10px] font-semibold text-white">
                {initials}
              </span>
              <span className="hidden min-w-0 max-w-[150px] flex-col leading-tight xl:flex">
                <span className="truncate text-xs font-semibold text-[#261D30]">{displayName}</span>
                {(companyLabel || membershipRole) && (
                  <span className="truncate text-[10px] text-[#73677E]">
                    {companyLabel}
                    {membershipRole ? ` · ${formatRole(membershipRole)}` : ''}
                  </span>
                )}
              </span>
            </div>
          )}

          {!sandbox && user && (
            <div
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[#E0DCE5] text-[#73677E] sm:hidden"
              aria-label={displayName}
            >
              <User size={18} />
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppNavbar;
